<template>
    <div class="simple-slider swiper" v-if="data && data.length">
        <div class="swiper-container" ref="slider">
            <div class="swiper-wrapper">
                <div
                    v-for="(slide, index) in data"
                    :key="index"
                    class="simple-slider__slide swiper-slide"
                >
                    <figure class="simple-slider__figure">
                        <img :src="slide.image" class="simple-slider__image" alt=""/>
                    </figure>
                </div>
            </div>
        </div>

        <div class="simple-slider__bottom">
            <div
                class="simple-slider__caption"
                v-html="data[currentSlide - 1]?.caption ?? ''"
            />
            <div class="simple-slider__controls">
                <div class="simple-slider__fraction" ref="fraction" v-html="sliderFraction"></div>

                <button class="simple-slider__nav-btn simple-slider__nav-btn--prev" ref="prev">
                    <z-icon
                        class="simple-slider__nav-btn-icon simple-slider__nav-btn--prev"
                        name="tools/play_m"
                        width="18"
                        height="18"
                        dir="down"
                    />
                </button>
                <button class="simple-slider__nav-btn simple-slider__nav-btn--next" ref="next">
                    <z-icon
                        class="simple-slider__nav-btn-icon simple-slider__nav-btn--prev"
                        name="tools/play_m"
                        width="18"
                        height="18"
                        dir="top"
                    />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swiper from 'swiper/swiper-bundle.mjs'

const props = defineProps({
    data: Array,
    autoHeight: {
        type: Boolean,
        default: false
    },
    swiperOptions: Object
})

const slider = ref(null)
const prev = ref(null)
const next = ref(null)

const swiper = ref(null)
const totalSlide = ref(null)
const currentSlide = ref(null)

const sliderFraction = computed(() => {
    if (!currentSlide.value || !totalSlide.value) return ''
    return `<span class="current-slide">${formatFraction(currentSlide.value)}</span><span class="total-slides">/${formatFraction(totalSlide.value)}</span>`
})

const init = () => {
    const options = {
        autoHeight: props.autoHeight,
        observer: true,
        observeParents: true,
        navigation: {
            prevEl: prev.value,
            nextEl: next.value
        },
        slidesPerView: 1,
        on: {
            slideChange: () => {
                currentSlide.value = swiper.value.activeIndex + 1
            }
        }
    }

    swiper.value = new Swiper(slider.value, Object.assign({}, props.swiperOptions, options))
}

const formatFraction = (num) => {
    return num > 9 ? num : '0' + num
}

onMounted(() => {
    if (props.data) {
        totalSlide.value = props.data.length
        currentSlide.value = 1
        init()
    }
})
</script>

<style lang="scss">
.simple-slider {
    position: relative;
    width: 100%;
    max-width: 100%;

    @include breakpoint (mobile) {
        width: auto;
    }

    &__slide {

        overflow: hidden;
    }

    &__figure {
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: var(--spacer-m);

        & img {
            border-radius: 20px;
        }
    }

    &__image {
        object-fit: cover;
    }

    &__bottom {
        margin-top: var(--spacer-m);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacer-s);

        @include breakpoint(mobile){
            flex-direction: column;
        }
    }

    &__caption {
        @include typo-level(M);
        line-height: 1.4;
        color: var(--color-black-80);
    }

    &__controls {
        margin-left: auto;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: var(--spacer-xs);
        z-index: 1;
    }

    &__nav-btn {
        padding: var(--spacer-xs);
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-primary);
        border: none;
        border-radius: 50%;

        .z-icon {
            & path {
                fill: var(--color-white);
            }
        }

        path {
            transition: fill $transition;
        }

        &.swiper-button-disabled {
            cursor: default;
            background: transparent;
            path {
               fill: var(--color-primary)
            }
        }
    }

    &__fraction {
        margin-right: 8px;
        min-width: 65px;
        font-weight: 400;
        color: var(--color-black-80);
        text-align: center;


        & .current-slide{
            font-size: 28px;
            line-height: 1.6;
        }

        & .total-slide {
            font-size: var(--typography-m);
            line-height: 1.2;
        }

    }
}
</style>
